import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchGuestWishlist, removeGuestWishlist } from '../../api/guestApi';
import { useToast } from '../../components/ToastContext';

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  const base = process.env.REACT_APP_API_BASE_URL || '';
  return `${base}${src.startsWith('/') ? src : `/${src}`}`;
};

const formatAmount = (currency, amount) => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return amount ? `${currency || ''} ${amount}`.trim() : 'Price on request';
  const prefix = currency ? `${currency} ` : 'Rs ';
  return `${prefix}${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const GuestWishlist = () => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchGuestWishlist();
      setItems(res.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load wishlist');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRemove = async (propertyId) => {
    if (!propertyId) return;
    setBusyId(propertyId);
    try {
      await removeGuestWishlist(propertyId);
      setItems((prev) => prev.filter((item) => item.property_id !== propertyId && item.id !== propertyId));
      toast.success('Removed from wishlist');
    } catch (err) {
      toast.error(err.message || 'Failed to remove');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, idx) => (
          <div key={idx} className="h-56 animate-pulse rounded-2xl border border-slate-200 bg-slate-50" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
        {error}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
        <p className="text-sm uppercase tracking-wide text-slate-400">Wishlist</p>
        <h2 className="mt-2 text-xl font-semibold text-slate-900">Shortlisted villas and stays will show here.</h2>
        <p className="mt-2 text-sm text-slate-500">Browse listings to start saving your favorites.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Saved stays</p>
          <h2 className="text-lg font-semibold text-slate-900">Wishlist</h2>
        </div>
        <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          {items.length} saved
        </span>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {items.map((item, idx) => {
          const image = resolveImage(item.image_url) || 'https://picsum.photos/seed/homevilas-wishlist/800/600';
          const title = item.title || 'Property';
          const location = item.location || 'India';
          const priceLabel = formatAmount(item.currency, item.price_per_night);
          const propertyId = item.property_id || item.id;
          const detailPath = propertyId ? `/property/${propertyId}` : '/stays';
          const cardKey = propertyId || `${title}-${idx}`;
          return (
            <article
              key={cardKey}
              className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
            >
              <div className="relative">
                <img src={image} alt={title} className="h-40 w-full object-cover" />
                <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                  Saved
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-2 px-4 py-4">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-slate-900">{title}</h3>
                  <p className="text-sm text-slate-500">{location}</p>
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  {priceLabel}
                  <span className="text-xs font-medium text-slate-500"> / night</span>
                </div>
                <div className="mt-auto flex items-center justify-between gap-2">
                  <Link
                    className="rounded-full border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:border-emerald-300"
                    to={detailPath}
                  >
                    View details
                  </Link>
                  <button
                    type="button"
                    className="rounded-full border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:border-rose-300 disabled:cursor-not-allowed disabled:opacity-70"
                    onClick={() => handleRemove(propertyId)}
                    disabled={busyId === propertyId}
                  >
                    {busyId === propertyId ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default GuestWishlist;

